/**
 * Permission-Katalog · Single Source of Truth für das Berechtigungs-Modal.
 *
 * Bildet alle Boolean-Permissions des Backends 1:1 ab und ergänzt sie um
 * UI-Metadaten (Icon, Klartext-Beschreibung, Kritikalitäts-Flag, Gruppen-Zuordnung).
 *
 * Backend-Quelle: `Domain/Entities/Transaction/UserPermissions.cs` (UserPermissionConfig).
 *
 * ─────────── DRIFT-SCHUTZ ───────────
 * Die `perm()`-Factory ist typgeneriert: das zweite Argument muss ein
 * Boolean-Property des entsprechenden Backend-Interfaces sein
 * (`EmployeePermissions`, `ShiftPermissions`, etc.). Wer ein Feld im
 * Backend umbenennt oder loescht, ohne hier nachzuziehen, bricht den
 * TypeScript-Build. Zusatz: `permission-dependencies.spec.ts` testet
 * runtime, dass jede Catalog-Permission im Default-Config existiert.
 */

import { UserPermissionConfig } from '../../model/user-permissions.model';

export type PermissionModule =
  | 'Employee'
  | 'Shift'
  | 'Tasks'
  | 'TimeTracking'
  | 'Absence'
  | 'Overtime'
  | 'Availability'
  | 'Department'
  | 'Reports'
  | 'Settings'
  | 'Datev'
  | 'Staffing'
  | 'MachineLearning'
  | 'Advanced';

export type PermissionGroup = 'operational' | 'system';

export interface PermissionDefinition {
  /** Backend-Modul (entspricht UserPermissionConfig-Property) */
  module: PermissionModule;
  /** Backend-Property-Name */
  key: string;
  /** i18n-Key für den Permission-Titel */
  labelKey: string;
  /** i18n-Key für die Klartext-Beschreibung darunter */
  descriptionKey: string;
  /** FontAwesome-Icon-Klasse */
  icon: string;
  /** Backend protokolliert + Token-Revoke bei Änderung */
  isCritical?: boolean;
  /** Sensible Daten (z.B. Gehalt) — UI markiert mit „Sensibel" */
  isSensitive?: boolean;
}

export interface ModuleDefinition {
  module: PermissionModule;
  group: PermissionGroup;
  labelKey: string;
  icon: string;
}

/**
 * Token-Revoke-Permissions (Backend protokolliert + invalidiert Refresh-Tokens).
 * Quelle: `UpdateUserPermissionsCommandHandler.CriticalPermissionsChanged()` Z. 170–194.
 */
export const CRITICAL_PERMISSIONS = new Set<string>([
  'Settings.CanManageUserPermissions',
  'Employee.CanDeleteEmployees',
  'Advanced.CanActAsOtherUser',
  'Advanced.CanAccessAPI',
  // DataScope.EmployeeDataScope wird separat im DataScope-Tab behandelt
]);

/**
 * Sensible Permissions (Audit-Log-pflichtig, keine Token-Revoke).
 */
export const SENSITIVE_PERMISSIONS = new Set<string>([
  'Employee.CanViewSalaryData',
  'Employee.CanEditSalaryData',
  'Employee.CanViewBankingData',
  'Employee.CanEditBankingData',
  'Employee.CanViewTaxData',
  'Employee.CanEditTaxData',
  'Employee.CanViewSocialInsuranceData',
  'Reports.CanViewFinancialReports',
  'Advanced.CanOverrideComplianceRules',
  'Advanced.CanBypassApprovalWorkflows',
]);

/**
 * Modul-Definitionen für die Sidebar (Reihenfolge bestimmt Sidebar-Reihenfolge).
 */
export const PERMISSION_MODULES: ModuleDefinition[] = [
  { module: 'Employee',         group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.EMPLOYEE',         icon: 'fa-light fa-users' },
  // Staffing (Personalbedarfsplanung) = Schritt 1 im Planungs-Workflow der
  // Haupt-App, direkt VOR Schichtplan (Schritt 2). Deshalb hier eingeordnet
  // — fachliche Reihenfolge "Wie viele Mitarbeiter wann?" → "Wer arbeitet wann?".
  { module: 'Staffing',         group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.STAFFING',         icon: 'fa-light fa-people-arrows' },
  { module: 'Shift',            group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.SHIFT',            icon: 'fa-light fa-calendar' },
  // Aufgaben folgen auf den Schichtplan: „Wer arbeitet wann?" → „Was ist dabei zu tun?".
  { module: 'Tasks',            group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.TASKS',            icon: 'fa-light fa-list-check' },
  { module: 'TimeTracking',     group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.TIME_TRACKING',    icon: 'fa-light fa-clock' },
  { module: 'Absence',          group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.ABSENCE',          icon: 'fa-light fa-umbrella-beach' },
  { module: 'Overtime',         group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.OVERTIME',         icon: 'fa-light fa-hourglass' },
  { module: 'Availability',     group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.AVAILABILITY',     icon: 'fa-light fa-calendar-check' },
  { module: 'Department',       group: 'operational', labelKey: 'USER_PERMISSIONS.TABS.DEPARTMENT',       icon: 'fa-light fa-sitemap' },
  { module: 'Reports',          group: 'system',      labelKey: 'USER_PERMISSIONS.TABS.REPORTS',          icon: 'fa-light fa-chart-line' },
  { module: 'Settings',         group: 'system',      labelKey: 'USER_PERMISSIONS.TABS.SETTINGS',         icon: 'fa-light fa-gear' },
  { module: 'Datev',            group: 'system',      labelKey: 'USER_PERMISSIONS.TABS.DATEV',            icon: 'fa-light fa-database' },
  { module: 'MachineLearning',  group: 'system',      labelKey: 'USER_PERMISSIONS.TABS.MACHINE_LEARNING', icon: 'fa-light fa-wand-magic-sparkles' },
  { module: 'Advanced',         group: 'system',      labelKey: 'USER_PERMISSIONS.TABS.ADVANCED',         icon: 'fa-light fa-shield-halved' },
];

/**
 * Permission-Katalog · alle Boolean-Permissions des Backends **ausser** den ausdrücklich
 * begründeten Ausnahmen in {@link PERMISSION_CATALOG_EXEMPT_KEYS}.
 * Reihenfolge innerhalb eines Moduls bestimmt die Anzeige-Reihenfolge.
 */
export const PERMISSION_CATALOG: PermissionDefinition[] = [
  // ───────────── EMPLOYEE (22 + 2 im Datenzugriff, s. u.) ─────────────
  perm('Employee', 'CanViewEmployeeList',          'fa-light fa-list'),
  perm('Employee', 'CanViewEmployeeDetails',       'fa-light fa-id-card'),
  perm('Employee', 'CanViewSalaryData',            'fa-light fa-euro-sign', { sensitive: true }),
  perm('Employee', 'CanViewSocialInsuranceData',   'fa-light fa-shield-heart', { sensitive: true }),
  perm('Employee', 'CanViewBankingData',           'fa-light fa-credit-card', { sensitive: true }),
  perm('Employee', 'CanViewTaxData',               'fa-light fa-file-invoice', { sensitive: true }),
  // `CanViewDocuments` und `CanManageDocuments` standen hier bis Ticket F-10.
  // Sie sind NICHT weggefallen — sie werden jetzt im Segment „Datenzugriff" gesetzt
  // (siehe PERMISSION_CATALOG_EXEMPT_KEYS unten). An ihrer Stelle steht in der Liste die
  // Zusammenfassungs-Zeile aus PERMISSION_POINTERS.
  perm('Employee', 'CanViewEmployeeHistory',       'fa-light fa-clock-rotate-left'),
  perm('Employee', 'CanEditBasicEmployeeData',     'fa-light fa-pen'),
  perm('Employee', 'CanEditSalaryData',            'fa-light fa-coins', { sensitive: true }),
  perm('Employee', 'CanEditTaxData',               'fa-light fa-percent', { sensitive: true }),
  perm('Employee', 'CanEditBankingData',           'fa-light fa-building-columns', { sensitive: true }),
  perm('Employee', 'CanEditContractData',          'fa-light fa-file-contract'),
  perm('Employee', 'CanCreateEmployees',           'fa-light fa-user-plus'),
  perm('Employee', 'CanDeleteEmployees',           'fa-light fa-user-xmark', { critical: true }),
  perm('Employee', 'CanDeactivateEmployees',       'fa-light fa-user-slash'),
  perm('Employee', 'CanInviteEmployees',           'fa-light fa-envelope'),
  perm('Employee', 'CanGeneratePins',              'fa-light fa-key'),
  perm('Employee', 'CanApproveChangeRequests',     'fa-light fa-check'),
  perm('Employee', 'CanRejectChangeRequests',      'fa-light fa-xmark'),
  perm('Employee', 'CanExportEmployeeData',        'fa-light fa-download'),
  perm('Employee', 'CanExportToExcel',             'fa-light fa-file-excel'),
  perm('Employee', 'CanExportToPdf',               'fa-light fa-file-pdf'),

  // ───────────── SHIFT (16) ─────────────
  perm('Shift', 'CanViewOwnShifts',                'fa-light fa-calendar'),
  perm('Shift', 'CanViewTeamShifts',               'fa-light fa-calendar-users'),
  perm('Shift', 'CanViewAllShifts',                'fa-light fa-calendar-days'),
  perm('Shift', 'CanViewShiftStatistics',          'fa-light fa-chart-line'),
  perm('Shift', 'CanCreateShifts',                 'fa-light fa-plus'),       // Legacy — durch CE5 Triple ersetzt, bleibt für Backward-Compat
  perm('Shift', 'CanEditShifts',                   'fa-light fa-pen'),         // Legacy
  perm('Shift', 'CanDeleteShifts',                 'fa-light fa-trash'),       // Legacy
  perm('Shift', 'CanPublishShifts',                'fa-light fa-paper-plane'), // Legacy
  perm('Shift', 'CanCopyShifts',                   'fa-light fa-copy'),
  // CE5 — dreistufige CRUD/Publish (12 neue Toggles)
  perm('Shift', 'CanCreateOwnShifts',              'fa-light fa-plus'),
  perm('Shift', 'CanCreateTeamShifts',             'fa-light fa-plus'),
  perm('Shift', 'CanCreateAllShifts',              'fa-light fa-circle-plus'),
  perm('Shift', 'CanEditOwnShifts',                'fa-light fa-pen'),
  perm('Shift', 'CanEditTeamShifts',               'fa-light fa-pen'),
  perm('Shift', 'CanEditAllShifts',                'fa-light fa-pen-to-square'),
  perm('Shift', 'CanDeleteOwnShifts',              'fa-light fa-trash'),
  perm('Shift', 'CanDeleteTeamShifts',             'fa-light fa-trash'),
  perm('Shift', 'CanDeleteAllShifts',              'fa-light fa-trash-can'),
  perm('Shift', 'CanPublishOwnShifts',             'fa-light fa-paper-plane'),
  perm('Shift', 'CanPublishTeamShifts',            'fa-light fa-paper-plane'),
  perm('Shift', 'CanPublishAllShifts',             'fa-light fa-paper-plane-top'),
  perm('Shift', 'CanManageOpenShifts',             'fa-light fa-clock'),
  perm('Shift', 'CanApproveShiftSwaps',            'fa-light fa-arrows-rotate'),
  perm('Shift', 'CanForceAssignShifts',            'fa-light fa-user-check'),
  perm('Shift', 'CanCreateTemplates',              'fa-light fa-file-plus'),
  perm('Shift', 'CanEditTemplates',                'fa-light fa-file-pen'),
  perm('Shift', 'CanDeleteTemplates',              'fa-light fa-file-xmark'),
  perm('Shift', 'CanApplyTemplates',               'fa-light fa-stamp'),

  // ───────────── TASKS (20) ─────────────
  // Reihenfolge wie im Backend-Modell: Ansehen, Anlegen, Bearbeiten, Löschen, Erledigen,
  // dann die Sonderrechte. Innerhalb jeder Tätigkeit Eigene → Team → Alle.
  perm('Tasks', 'CanViewOwnTasks',                 'fa-light fa-list-check'),
  perm('Tasks', 'CanViewTeamTasks',                'fa-light fa-users'),
  perm('Tasks', 'CanViewAllTasks',                 'fa-light fa-building'),
  perm('Tasks', 'CanCreateOwnTasks',               'fa-light fa-plus'),
  perm('Tasks', 'CanCreateTeamTasks',              'fa-light fa-user-plus'),
  perm('Tasks', 'CanCreateAllTasks',               'fa-light fa-square-plus'),
  perm('Tasks', 'CanEditOwnTasks',                 'fa-light fa-pen'),
  perm('Tasks', 'CanEditTeamTasks',                'fa-light fa-pen-to-square'),
  perm('Tasks', 'CanEditAllTasks',                 'fa-light fa-pen-ruler'),
  perm('Tasks', 'CanDeleteOwnTasks',               'fa-light fa-box-archive'),
  perm('Tasks', 'CanDeleteTeamTasks',              'fa-light fa-folder-xmark'),
  perm('Tasks', 'CanDeleteAllTasks',               'fa-light fa-trash-xmark'),
  perm('Tasks', 'CanCompleteOwnTasks',             'fa-light fa-square-check'),
  perm('Tasks', 'CanCompleteTeamTasks',            'fa-light fa-users-medical'),
  perm('Tasks', 'CanCompleteAllTasks',             'fa-light fa-circle-check'),
  perm('Tasks', 'CanAssignTasksToOthers',          'fa-light fa-share-nodes'),
  perm('Tasks', 'CanManageTaskRoutines',           'fa-light fa-repeat'),
  perm('Tasks', 'CanViewTaskCompliance',           'fa-light fa-clipboard-check'),
  perm('Tasks', 'CanExportTaskCompliance',         'fa-light fa-file-export'),
  // „Im Namen einer anderen Person abhaken" — dieselbe Klasse wie Schichten erzwingen.
  perm('Tasks', 'CanActOnBehalfForTasks',          'fa-light fa-user-secret'),

  // ───────────── TIME_TRACKING (19) ─────────────
  perm('TimeTracking', 'CanViewOwnTime',           'fa-light fa-user-clock'),
  perm('TimeTracking', 'CanEditOwnTime',           'fa-light fa-pen'),
  perm('TimeTracking', 'CanDeleteOwnTime',         'fa-light fa-trash'),
  perm('TimeTracking', 'CanSubmitTimeCorrection',  'fa-light fa-paper-plane'),
  perm('TimeTracking', 'CanViewTeamTime',          'fa-light fa-users'),
  perm('TimeTracking', 'CanViewAllTime',           'fa-light fa-clock'),
  // Die Mehrtages-Abfrage (Wochen-Timeline, Zeitraumlisten). Der Server prüft sie in
  // `GetClockEventsForMultipleDaysQueryHandler` — ein wirksames Recht, das bis zum
  // 19.09.2026 in keiner Oberfläche vergebbar war. Derselbe Fall wie seinerzeit die
  // 20 Aufgaben-Rechte: es kam vom Server, lief im Speichern unverändert zurück und
  // konnte von niemandem gesetzt werden.
  perm('TimeTracking', 'CanViewTimeRecords',       'fa-light fa-calendar-days'),
  perm('TimeTracking', 'CanEditTeamTime',          'fa-light fa-pen'),         // CE2
  perm('TimeTracking', 'CanEditAllTime',           'fa-light fa-pen-to-square'),
  perm('TimeTracking', 'CanDeleteTeamTime',        'fa-light fa-trash'),       // CE2
  perm('TimeTracking', 'CanDeleteAllTime',         'fa-light fa-trash-can'),
  perm('TimeTracking', 'CanApproveTimeCorrections', 'fa-light fa-circle-check'),
  perm('TimeTracking', 'CanApproveOvertime',       'fa-light fa-hourglass'),
  perm('TimeTracking', 'CanManageTerminal',        'fa-light fa-terminal'),
  perm('TimeTracking', 'CanViewTerminalLogs',      'fa-light fa-list-check'),
  perm('TimeTracking', 'CanExportTimeData',        'fa-light fa-download'),
  perm('TimeTracking', 'CanExportTimesheets',      'fa-light fa-file-export'),
  // ── Periodenabschluss (BE-11, FE-10) ────────────────────────────────────────────────
  // ⚠️ Beide serverseitig Default `false` — anders als fast jedes Recht darüber. Ohne den
  // ersten Schalter ist der Monatsabschluss für NIEMANDEN erreichbar: am 19.09.2026 antwortete
  // `POST ClosePeriod` auch dem Firmeninhaber mit `403 PERIOD_LOCK_NOT_ALLOWED`.
  perm('TimeTracking', 'CanManagePeriodLocks',     'fa-light fa-lock', { sensitive: true }),
  // Das schärfere der beiden: Wer es hat, schreibt in einen abgerechneten Monat. Jede Nutzung
  // erhöht `breachCount` der Sperre und erscheint in der Abschlussliste.
  //
  // ⚠️ Bewusst `sensitive` und NICHT `critical`: Das Abzeichen für `critical` lautet wörtlich
  // „Kritisch · Token-Revoke", und genau das passiert hier nicht — `CriticalPermissionsChanged`
  // im Backend führt dieses Recht nicht. Ein Etikett, das etwas Konkretes verspricht, muss
  // stimmen; sonst wartet jemand auf eine Abmeldung, die nie kommt.
  perm('TimeTracking', 'CanBreakPeriodLock',       'fa-light fa-lock-open', { sensitive: true }),

  // ───────────── ABSENCE (13) ─────────────
  perm('Absence', 'CanViewOwnAbsences',            'fa-light fa-calendar-xmark'),
  perm('Absence', 'CanRequestAbsence',             'fa-light fa-circle-plus'),
  perm('Absence', 'CanEditOwnAbsence',             'fa-light fa-pen'),
  perm('Absence', 'CanCancelOwnAbsence',           'fa-light fa-ban'),
  perm('Absence', 'CanViewTeamAbsences',           'fa-light fa-users'),
  perm('Absence', 'CanViewAllAbsences',            'fa-light fa-calendar'),
  perm('Absence', 'CanEditTeamAbsences',           'fa-light fa-pen'),         // CE3
  perm('Absence', 'CanEditAllAbsences',            'fa-light fa-pen-to-square'),
  perm('Absence', 'CanDeleteTeamAbsences',         'fa-light fa-trash'),       // CE3
  perm('Absence', 'CanDeleteAllAbsences',          'fa-light fa-trash-can'),   // CE3
  perm('Absence', 'CanApproveTeamAbsences',        'fa-light fa-check'),       // CE3
  perm('Absence', 'CanApproveAbsences',            'fa-light fa-check'),
  perm('Absence', 'CanRejectTeamAbsences',         'fa-light fa-xmark'),       // CE3
  perm('Absence', 'CanRejectAbsences',             'fa-light fa-xmark'),
  perm('Absence', 'CanOverrideAbsenceRules',       'fa-light fa-gavel'),
  perm('Absence', 'CanManageVacationDays',         'fa-light fa-umbrella-beach'),
  perm('Absence', 'CanManageSpecialLeave',         'fa-light fa-star'),
  perm('Absence', 'CanManageSickLeave',            'fa-light fa-hospital'),

  // ───────────── OVERTIME (7) ─────────────
  perm('Overtime', 'CanViewOwnOvertime',           'fa-light fa-user-clock'),
  perm('Overtime', 'CanViewTeamOvertime',          'fa-light fa-users'),
  perm('Overtime', 'CanViewAllOvertime',           'fa-light fa-clock'),
  perm('Overtime', 'CanApproveOvertime',           'fa-light fa-check'),
  perm('Overtime', 'CanEditOvertime',              'fa-light fa-pen'),
  perm('Overtime', 'CanExportOvertimeReports',     'fa-light fa-file-export'),
  perm('Overtime', 'CanManageCompensation',        'fa-light fa-coins'),

  // ───────────── AVAILABILITY (5) ─────────────
  perm('Availability', 'CanViewOwnAvailability',   'fa-light fa-user-clock'),
  perm('Availability', 'CanViewTeamAvailability',  'fa-light fa-users'),
  perm('Availability', 'CanViewAllAvailability',   'fa-light fa-calendar-check'),
  perm('Availability', 'CanEditOwnAvailability',   'fa-light fa-pen'),
  perm('Availability', 'CanEditTeamAvailability',  'fa-light fa-pen'),         // CE4
  perm('Availability', 'CanEditAllAvailability',   'fa-light fa-pen-to-square'),

  // ───────────── DEPARTMENT (11) ─────────────
  perm('Department', 'CanViewDepartments',         'fa-light fa-eye'),
  perm('Department', 'CanCreateDepartments',       'fa-light fa-plus'),
  perm('Department', 'CanEditDepartments',         'fa-light fa-pen'),
  perm('Department', 'CanDeleteDepartments',       'fa-light fa-trash'),
  perm('Department', 'CanManageProjects',          'fa-light fa-diagram-project'),
  perm('Department', 'CanAssignEmployeesToDepartments', 'fa-light fa-user-plus'),
  perm('Department', 'CanViewSkills',              'fa-light fa-graduation-cap'),
  perm('Department', 'CanCreateSkills',            'fa-light fa-circle-plus'),
  perm('Department', 'CanEditSkills',              'fa-light fa-pen-to-square'),
  perm('Department', 'CanDeleteSkills',            'fa-light fa-trash-can'),
  perm('Department', 'CanAssignSkills',            'fa-light fa-user-tag'),

  // ───────────── REPORTS (11) ─────────────
  perm('Reports', 'CanViewBasicReports',           'fa-light fa-chart-simple'),
  perm('Reports', 'CanViewFinancialReports',       'fa-light fa-chart-pie', { sensitive: true }),
  perm('Reports', 'CanViewTimeReports',            'fa-light fa-clock'),
  perm('Reports', 'CanViewAbsenceReports',         'fa-light fa-calendar-xmark'),
  perm('Reports', 'CanViewProductivityReports',    'fa-light fa-chart-line'),
  perm('Reports', 'CanViewComplianceReports',      'fa-light fa-shield-check'),
  perm('Reports', 'CanCreateCustomReports',        'fa-light fa-plus'),
  perm('Reports', 'CanScheduleReports',            'fa-light fa-calendar-clock'),
  perm('Reports', 'CanShareReports',               'fa-light fa-share'),
  perm('Reports', 'CanExportReports',              'fa-light fa-download'),
  perm('Reports', 'CanPrintReports',               'fa-light fa-print'),

  // ───────────── SETTINGS (12) ─────────────
  perm('Settings', 'CanViewCompanySettings',       'fa-light fa-eye'),
  perm('Settings', 'CanEditCompanySettings',       'fa-light fa-building'),
  perm('Settings', 'CanEditWorkingTimeSettings',   'fa-light fa-clock'),
  perm('Settings', 'CanEditNotificationSettings',  'fa-light fa-bell'),
  perm('Settings', 'CanEditComplianceSettings',    'fa-light fa-shield'),
  perm('Settings', 'CanManageIntegrations',        'fa-light fa-plug'),
  perm('Settings', 'CanManageSubscription',        'fa-light fa-credit-card'),
  perm('Settings', 'CanManagePayments',            'fa-light fa-money-bill'),
  perm('Settings', 'CanManageUserPermissions',     'fa-light fa-user-shield', { critical: true }),
  perm('Settings', 'CanViewAuditLogs',             'fa-light fa-list-check'),
  perm('Settings', 'CanManageCompanyHolidays',     'fa-light fa-calendar'),
  perm('Settings', 'CanImportHolidays',            'fa-light fa-upload'),

  // ───────────── DATEV (7) ─────────────
  perm('Datev', 'CanViewDatevSettings',            'fa-light fa-eye'),
  perm('Datev', 'CanConfigureDatevSettings',       'fa-light fa-gear'),
  perm('Datev', 'CanExportDatevMasterData',        'fa-light fa-database'),
  perm('Datev', 'CanExportDatevMovementData',      'fa-light fa-arrows-rotate'),
  perm('Datev', 'CanImportDatevData',              'fa-light fa-upload'),
  perm('Datev', 'CanManageDatevProfiles',          'fa-light fa-id-card'),
  perm('Datev', 'CanScheduleDatevExports',         'fa-light fa-calendar-clock'),

  // ───────────── STAFFING (10) ─────────────
  perm('Staffing', 'CanViewStaffingTemplates',     'fa-light fa-eye'),
  perm('Staffing', 'CanCreateStaffingTemplates',   'fa-light fa-plus'),
  perm('Staffing', 'CanEditStaffingTemplates',     'fa-light fa-pen'),
  perm('Staffing', 'CanDeleteStaffingTemplates',   'fa-light fa-trash'),
  perm('Staffing', 'CanViewStaffingRequirements',  'fa-light fa-clipboard-list'),
  perm('Staffing', 'CanCreateStaffingRequirements', 'fa-light fa-circle-plus'),
  perm('Staffing', 'CanEditStaffingRequirements',  'fa-light fa-pen-to-square'),
  perm('Staffing', 'CanApproveStaffingRequirements', 'fa-light fa-check'),
  perm('Staffing', 'CanUseStaffingOptimization',   'fa-light fa-wand-magic-sparkles'),
  perm('Staffing', 'CanOverrideStaffingRules',     'fa-light fa-gavel'),

  // ───────────── MACHINE_LEARNING (6) ─────────────
  perm('MachineLearning', 'CanViewMLInsights',     'fa-light fa-chart-mixed'),
  perm('MachineLearning', 'CanConfigureMLSettings', 'fa-light fa-gears'),
  perm('MachineLearning', 'CanTriggerMLTraining',  'fa-light fa-play'),
  perm('MachineLearning', 'CanApproveMLSuggestions', 'fa-light fa-check-double'),
  perm('MachineLearning', 'CanViewMLMetrics',      'fa-light fa-chart-line'),
  perm('MachineLearning', 'CanExportMLData',       'fa-light fa-download'),

  // ───────────── ADVANCED (8 booleans) ─────────────
  perm('Advanced', 'CanAccessAPI',                 'fa-light fa-code', { critical: true }),
  perm('Advanced', 'CanUseWebhooks',               'fa-light fa-webhook'),
  perm('Advanced', 'CanManageAPIKeys',             'fa-light fa-key'),
  perm('Advanced', 'CanOverrideComplianceRules',   'fa-light fa-shield-xmark', { sensitive: true }),
  perm('Advanced', 'CanBypassApprovalWorkflows',   'fa-light fa-forward', { sensitive: true }),
  perm('Advanced', 'CanActAsOtherUser',            'fa-light fa-user-secret', { critical: true }),
  perm('Advanced', 'IsReadOnlyMode',               'fa-light fa-lock'),
  perm('Advanced', 'RequiresTwoFactorAuth',        'fa-light fa-mobile-screen'),
];

/**
 * Boolean-Rechte, die **absichtlich nicht** im {@link PERMISSION_CATALOG} stehen.
 *
 * Sie bleiben Teil des Feldvertrags und des PUT-Bodys — nur die Rechte-**Liste** führt sie nicht
 * mehr (Vertrag `x-planner-server/plans/document-access-scope-contract.md` § 1.3, Ticket F-10).
 * Zwei Orte für dieselbe Sache wären genau die Verwirrung, die der Umbau vermeiden soll.
 *
 * Wo sie stattdessen gesetzt werden:
 *
 * | Recht | Ort | Warum es weiter gebraucht wird |
 * |---|---|---|
 * | `Employee.CanManageDocuments` | ausdrücklicher Schalter „Personalverwaltung" im Fuss der Aktenteil-Matrix (`document-section-matrix`, Output `canManageDocumentsChange`) | Torwächter für Aktenteil 4 und 5, für `Delete` (Stufe `Write` allein genügt nicht) und für `ManagePermissions`, also alle Einzelfreigaben (§ 7.1) |
 * | `Employee.CanViewDocuments` | **abgeleitet** aus der Matrix: mindestens ein Aktenteil ≥ `Read` ⇒ `true` (Output `canViewDocumentsChange`) | Torwächter für `ViewAudit` (§ 7.1, § 7.2 Fussnote 2) |
 *
 * Diese Liste ist eine **Ausnahme**, kein Abschalten des Drift-Schutzes: die Invariante
 * „jedes Boolean-Feld des Modells steht im Katalog" (`permission-dependencies.vitest.spec.ts`)
 * prüft weiter alle übrigen Felder, ein **drittes** versehentlich fehlendes Feld fällt also
 * unverändert auf. Wer hier etwas ergänzt, braucht einen belegten zweiten Setz-Ort.
 */
export const PERMISSION_CATALOG_EXEMPT_KEYS: readonly string[] = [
  'Employee.CanViewDocuments',
  'Employee.CanManageDocuments',
];

/**
 * Zeiger-Zeilen: Einträge, die in der Rechte-Liste erscheinen, aber **kein** eigenes Recht
 * schalten — sie nennen den Stand und verweisen auf den Ort, an dem er eingestellt wird.
 *
 * Aktuell genau einer: die Zusammenfassungs-Zeile „Personalakte", die die zwei Häkchen aus
 * {@link PERMISSION_CATALOG_EXEMPT_KEYS} ersetzt (Designvorschlag
 * `docs/design-system/document-access-scope-proposal.html`, Abschnitt „Wo es im Reiter
 * ‚Berechtigungen' auftaucht").
 *
 * Der `key` ist bewusst **kein** Feldname des Modells (sonst wäre es ein Recht). Er dient als
 * stabile Kennung für die DOM-Id (`perm-row-Employee-DocumentAccessSummary`), den
 * Such-Treffer und den Anspring-Puls — dieselbe Form wie bei echten Zeilen, damit die
 * Such-Infrastruktur ohne Sonderfall funktioniert.
 */
export const DOCUMENT_ACCESS_SUMMARY_POINTER: PermissionDefinition = {
  module: 'Employee',
  key: 'DocumentAccessSummary',
  labelKey: 'USER_PERMISSIONS.V2.DOC_SUMMARY.TITLE',
  descriptionKey: 'USER_PERMISSIONS.V2.DOC_SUMMARY.DESC',
  icon: 'fa-light fa-folder-open',
  isCritical: false,
  isSensitive: false,
};

export const PERMISSION_POINTERS: readonly PermissionDefinition[] = [
  DOCUMENT_ACCESS_SUMMARY_POINTER,
];

/**
 * Filtert die Boolean-Felder eines Permission-Block-Interfaces. Wird in
 * der `perm()`-Factory benutzt, um den `key`-Parameter typsicher zu
 * machen — nur Boolean-Properties des entsprechenden Backend-Interfaces
 * sind erlaubt, alles andere (`ScopeLevel`-Enums, Arrays) wird vom
 * Compiler abgelehnt.
 */
type BoolKeyOf<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends boolean ? K : never;
}[keyof T] &
  string;

/**
 * Faktor-Helper · konstruiert eine PermissionDefinition mit normalisierten i18n-Keys.
 *
 * Generisches Typing: der `key`-Parameter wird gegen die Boolean-Felder
 * des zugehoerigen Backend-Interfaces validiert. Tippfehler oder
 * entfernte Backend-Felder = TypeScript-Fehler.
 */
function perm<M extends PermissionModule, K extends BoolKeyOf<UserPermissionConfig[M]>>(
  module: M,
  key: K,
  icon: string,
  flags?: { critical?: boolean; sensitive?: boolean }
): PermissionDefinition {
  const moduleI18n = moduleToI18n(module);
  return {
    module,
    key,
    labelKey:       `USER_PERMISSIONS.${moduleI18n}.${camelToSnakeUpper(key)}`,
    descriptionKey: `USER_PERMISSIONS.V2.DESCRIPTION.${moduleI18n}.${camelToSnakeUpper(key)}`,
    icon,
    isCritical: flags?.critical ?? false,
    isSensitive: flags?.sensitive ?? false,
  };
}

function moduleToI18n(module: PermissionModule): string {
  if (module === 'TimeTracking') return 'TIME_TRACKING';
  if (module === 'MachineLearning') return 'ML';
  return module.toUpperCase();
}

function camelToSnakeUpper(camel: string): string {
  return camel.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
}

/**
 * Liefert alle Permissions eines Moduls in Anzeige-Reihenfolge.
 */
export function getPermissionsForModule(module: PermissionModule): PermissionDefinition[] {
  return PERMISSION_CATALOG.filter((p) => p.module === module);
}

/**
 * Prüft, ob ein Permission-Key als kritisch markiert ist (Token-Revoke).
 */
export function isCriticalPermissionKey(module: PermissionModule, key: string): boolean {
  return CRITICAL_PERMISSIONS.has(`${module}.${key}`);
}

/**
 * Prüft, ob ein Permission-Key als sensibel markiert ist (Audit-Log).
 */
export function isSensitivePermissionKey(module: PermissionModule, key: string): boolean {
  return SENSITIVE_PERMISSIONS.has(`${module}.${key}`);
}
